import { useRouter } from 'next/router';
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  InputAdornment,
  IconButton,
  Autocomplete,
} from '@mui/material';
import FormatsService from 'services/FormatsService';
import { useStyles } from './styles';
import useIsMobile from 'hooks/useIsMobile';
import TemplatesService from 'services/TemplatesService';
import NextLink from 'next/link';
import { Icons } from 'components/Icons';
import { COLOR_GRAY_70 } from 'styles/variables/colors';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';

const Props = {
  children: PropTypes.node,
  filters: PropTypes.shape({
    format: PropTypes.string,
    search: PropTypes.string,
  }),
};

export default function Header({ children, filters: defaultFilters }) {
  const isMobile = useIsMobile();
  const classes = useStyles();
  const inputElement = useRef(undefined);
  const router = useRouter();
  const slugPage = router.pathname === '/infographic-templates/[slug]';
  const [formats, setFormats] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [filters, setFilters] = useState({
    format: 'all',
    search: '',
  });

  const orderedFormats = ['Graphical Abstracts', 'Slides Presentations', 'Infographics', 'Posters'];

  const formatsOrdered = [...formats].sort((a, b) => {
    const indexA = orderedFormats.indexOf(a.name);
    const indexB = orderedFormats.indexOf(b.name);
    return indexA - indexB;
  });

  async function fetchFormats() {
    try {
      const { data } = await FormatsService.fetchAll();
      setFormats(data);
    } catch (e) {
      console.error(e);
    }
  }

  async function handleInputChange(event, newValue) {
    const formattedSearch =
      newValue && newValue.length > 0 ? newValue.charAt(0).toUpperCase() + newValue.slice(1) : '';

    setFilters({ ...filters, search: formattedSearch });

    try {
      const {
        data: { data },
      } = await TemplatesService.fetchAll({
        search: formattedSearch !== '' ? formattedSearch : undefined,
      });

      const themeNames = data.map((theme) => theme.theme_name);
      const filteredSuggestions = themeNames.filter((themeName) => themeName.startsWith(newValue));
      const limitedSuggestions = filteredSuggestions.slice(0, 5);

      setSuggestions(limitedSuggestions);
    } catch (error) {
      console.error(error);
    }
  }

  function handleSearchSubmit(event) {
    event.preventDefault();

    const query = { search: filters.search };

    if (filters.format !== 'all') {
      query.format = filters.format;
    }

    router.push({
      pathname: '/infographic-templates/search',
      query,
    });
  }

  useEffect(() => {
    fetchFormats();
  }, []);

  useEffect(() => {
    if (defaultFilters) {
      setFilters({
        format: defaultFilters.format || 'all',
        search: defaultFilters.search || '',
      });
    }
  }, [defaultFilters]);

  return (
    <Box pt={!isMobile ? 4 : 3} pb={!isMobile ? 3 : 2}>
      <Container>
        {!slugPage && (
          <Box mb={2} textAlign="center">
            <Typography variant="h5" className={classes.primaryColor} gutterBottom={true}>
              <b>Infographic templates made by scientists</b>
            </Typography>

            <Typography>
              You don't have to start from zero, select the best fitting option out of our brilliant
              infographic templates, <br /> customize if needed and download, present or share your
              work straight away.
            </Typography>
          </Box>
        )}

        {slugPage ? (
          <Box
            className={`${slugPage && classes.boxContentSearch} ${
              classes.boxContentSearchTemplate
            }`}
          >
            <Box className={classes.titleSearch} justifyContent="space-between">
              {slugPage && (
                <NextLink href="/infographic-templates/" prefetch={false} passHref={true}>
                  <div className={classes.btnBack}>
                    <Icons.arrowBack style={{ color: COLOR_GRAY_70, height: 16 }} />
                    <span>Back to results</span>
                  </div>
                </NextLink>
              )}
              <Typography variant="h6" align="center">
                <b>Find the perfect infographic template for you!</b>
              </Typography>
            </Box>

            <form className={classes.form} onSubmit={handleSearchSubmit}>
              <Box maxWidth={280}>
                <Autocomplete
                  freeSolo
                  options={suggestions}
                  value={filters.search}
                  onChange={(event, newValue) => handleInputChange(event, newValue)}
                  onInputChange={handleInputChange}
                  className={classes.inputSearch}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      ref={inputElement}
                      variant="outlined"
                      label="Search for a subject, like “DNA”"
                      required
                      InputLabelProps={{ required: false }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end" style={{ marginRight: -25 }}>
                            <IconButton
                              aria-label="search templates"
                              edge="end"
                              type="submit"
                              disabled={!filters.search}
                              size="large"
                            >
                              <Icons.search />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </form>
          </Box>
        ) : (
          <>
            <Box display="flex" justifyContent="center">
              <Typography variant="h6" align="center" gutterBottom={true}>
                <b>Find the perfect infographic template for you</b>
              </Typography>
            </Box>

            <form className={classes.form} onSubmit={handleSearchSubmit}>
              <InputLabel id="format-select-label" className={classes.inputLabel}>
                Format
              </InputLabel>
              <Select
                className={classes.input}
                select={true}
                variant="outlined"
                label="Format"
                id="format-select"
                labelId="format-select-label"
                name="formatSelect"
                value={formats.length ? filters.format : ''}
                onChange={({ target: { value } }) => setFilters({ ...filters, format: value })}
                inputProps={{
                  'aria-labelledby': 'format-select-all',
                  role: 'combobox',
                  'aria-expanded': Boolean(filters.format),
                  'aria-controls': 'format-menu',
                  'aria-autocomplete': 'none',
                  'aria-activedescendant': filters.format ? `format-select-${filters.format}` : '',
                  'aria-labelledby': 'format-select-label',
                }}
                renderValue={(selected) => (
                  <div
                    role="textbox"
                    aria-readonly="true"
                    tabIndex={-1}
                    aria-label="Selected format"
                  >
                    {selected === 'all'
                      ? 'All formats'
                      : formatsOrdered.find((f) => f.slug === selected)?.name || ''}
                  </div>
                )}
                MenuProps={{
                  id: 'format-menu',
                }}
              >
                <MenuItem id="format-select-all" value="all" role="option">
                  All formats
                </MenuItem>

                {formatsOrdered.map((format, index) => (
                  <MenuItem
                    key={format.name}
                    id={`format-select-${format.slug}`}
                    value={format.slug}
                    role="option"
                    aria-selected={filters.format === format.slug}
                  >
                    {format.name}
                  </MenuItem>
                ))}
              </Select>

              <Box maxWidth={280}>
                <Autocomplete
                  freeSolo
                  options={suggestions}
                  value={filters.search}
                  onChange={(event, newValue) => handleInputChange(event, newValue)}
                  onInputChange={handleInputChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      ref={inputElement}
                      className={classes.input}
                      variant="outlined"
                      label="Search"
                      required
                      InputLabelProps={{ required: false }}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <InputAdornment position="end" style={{ marginRight: -25 }}>
                            <IconButton
                              aria-label="search templates"
                              edge="end"
                              type="submit"
                              disabled={!filters.search}
                              size="large"
                            >
                              <Icons.search />
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                />
              </Box>
            </form>
          </>
        )}

        {children}
      </Container>
    </Box>
  );
}

Header.propTypes = Props;
